import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchVehicles, createRental, createAgreement, sendAgreement } from "@/lib/api";
import { RENT_MODES, RENT_MODE_LABELS, TIME_PERIODS, TIME_PERIOD_LABELS, VEHICLE_TYPES, VEHICLE_TYPE_LABELS, formatDateTime } from "@/lib/constants";
import { getErrorMessage } from "@/lib/utils";
import { Check, CaretLeft, CaretRight, Car, CurrencyDollar, EnvelopeSimple, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = [
  { key: "vehicle", label: "Vehicle", icon: Car },
  { key: "billing", label: "Billing", icon: CurrencyDollar },
  { key: "customer", label: "Customer", icon: EnvelopeSimple },
  { key: "review", label: "Review", icon: ClipboardText },
];

const toDateTimeLocalValue = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getDefaultTerms = () => {
  const pickup = new Date();
  pickup.setMinutes(0, 0, 0);
  pickup.setHours(pickup.getHours() + 1);

  const plannedReturn = new Date(pickup);
  plannedReturn.setDate(plannedReturn.getDate() + 7);

  return {
    rent_mode: "FIXED_TERM",
    time_period: "DAILY",
    pickup_datetime: toDateTimeLocalValue(pickup),
    return_datetime: toDateTimeLocalValue(plannedReturn),
    pickup_branch: "",
    return_branch: "",
    bond_amount: 500,
    daily_rate: 0,
    weekly_rate: 0,
    notes: "",
  };
};

function InputField({ label, testId, hint, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>
      <input data-testid={testId} className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black bg-white font-medium" {...props} />
      {hint ? <p className="mt-1.5 text-xs text-slate-400 font-medium">{hint}</p> : null}
    </div>
  );
}

export default function NewRentalPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleType, setVehicleType] = useState("ALL");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [customerEmail, setCustomerEmail] = useState("");
  const [terms, setTerms] = useState(getDefaultTerms);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchVehicles({ status: "AVAILABLE" }).then(setVehicles).catch(() => toast.error("Failed to load vehicles"));
  }, []);

  useEffect(() => {
    if (!selectedVehicle) return;
    setTerms((current) => ({
      ...current,
      pickup_branch: current.pickup_branch || selectedVehicle.branch || "",
      return_branch: current.return_branch || selectedVehicle.branch || "",
      daily_rate: current.daily_rate || selectedVehicle.daily_rate || 0,
      weekly_rate: current.weekly_rate || Math.round((selectedVehicle.daily_rate || 0) * 7 * 100) / 100,
    }));
  }, [selectedVehicle]);

  const filteredVehicles = useMemo(() => (
    vehicleType === "ALL" ? vehicles : vehicles.filter((vehicle) => vehicle.type === vehicleType)
  ), [vehicleType, vehicles]);

  const termsValidationError = useMemo(() => {
    const pickupAt = new Date(terms.pickup_datetime);
    if (Number.isNaN(pickupAt.getTime())) {
      return "Enter a valid pickup date and time.";
    }
    if (terms.rent_mode === "FIXED_TERM") {
      const returnAt = new Date(terms.return_datetime);
      if (!terms.return_datetime || Number.isNaN(returnAt.getTime())) {
        return "Fixed term rentals require a planned return date.";
      }
      if (returnAt <= pickupAt) {
        return "Planned return must be after pickup.";
      }
    }
    return "";
  }, [terms]);

  const canProceed = () => {
    if (step === 0) return Boolean(selectedVehicle);
    if (step === 1) {
      if (termsValidationError) return false;
      if (terms.rent_mode === "FIXED_TERM" && !terms.time_period) return false;
      if (terms.rent_mode === "FIXED_TERM" && terms.time_period === "DAILY") return Number(terms.daily_rate) > 0;
      return Number(terms.weekly_rate) > 0;
    }
    if (step === 2) return /\S+@\S+\.\S+/.test(customerEmail);
    return true;
  };

  const handleCreate = async () => {
    if (!selectedVehicle) return;
    setCreating(true);
    try {
      const rental = await createRental({
        vehicle_id: selectedVehicle.id,
        customer_email: customerEmail.trim().toLowerCase(),
        rent_mode: terms.rent_mode,
        time_period: terms.rent_mode === "PAYG" ? null : terms.time_period,
        pickup_datetime: terms.pickup_datetime,
        return_datetime: terms.rent_mode === "FIXED_TERM" ? terms.return_datetime : "",
        pickup_branch: terms.pickup_branch,
        return_branch: terms.return_branch,
        bond_amount: Number(terms.bond_amount) || 0,
        daily_rate: Number(terms.daily_rate) || 0,
        weekly_rate: Number(terms.weekly_rate) || 0,
        notes: terms.notes,
      });
      const agreement = await createAgreement(rental.id);
      const sendResult = await sendAgreement(agreement.id);
      toast.success(sendResult.message || `Rental ${rental.rental_number} created`);
      navigate(`/rentals/${rental.id}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to create rental"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="new-rental-page">
      <div className="max-w-5xl mx-auto p-6 lg:p-8 space-y-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Create Rental</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Staff creates the rental shell, then the customer completes their details and signs from the same link.</p>
        </div>

        <div className="flex items-center gap-2">
          {STEPS.map((stepItem, index) => {
            const Icon = stepItem.icon;
            const isComplete = index < step;
            const isCurrent = index === step;
            return (
              <div key={stepItem.key} className="flex items-center gap-2 flex-1">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${isComplete ? "step-complete" : isCurrent ? "step-active" : "step-inactive"}`}>
                  {isComplete ? <Check weight="bold" className="w-4 h-4" /> : <Icon weight="bold" className="w-4 h-4" />}
                </div>
                <span className={`text-xs font-bold hidden sm:block ${isCurrent ? "text-slate-900" : "text-slate-400"}`}>{stepItem.label}</span>
                {index < STEPS.length - 1 ? <div className={`flex-1 h-0.5 ${isComplete ? "bg-emerald-500" : "bg-slate-200"}`} /> : null}
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 lg:p-8 min-h-[420px]">
          {step === 0 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold font-heading text-slate-900">Select Vehicle</h2>
                <p className="text-sm text-slate-500 mt-1">Choose a vehicle type first, then select the exact vehicle to hire.</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button data-testid="vehicle-type-all" onClick={() => setVehicleType("ALL")} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${vehicleType === "ALL" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>All Types</button>
                {VEHICLE_TYPES.map((type) => (
                  <button
                    key={type}
                    data-testid={`vehicle-type-${type.toLowerCase()}`}
                    onClick={() => setVehicleType(type)}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${vehicleType === type ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >
                    {VEHICLE_TYPE_LABELS[type]}
                  </button>
                ))}
              </div>

              {filteredVehicles.length === 0 ? (
                <div className="text-center py-12 text-slate-400">No available vehicles for this type.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredVehicles.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      data-testid={`vehicle-select-${vehicle.registration}`}
                      onClick={() => setSelectedVehicle(vehicle)}
                      className={`text-left border rounded-md p-4 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md ${selectedVehicle?.id === vehicle.id ? "border-black ring-2 ring-black/10 bg-foreground/5" : "border-slate-200 bg-white"}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded vtype-${vehicle.type.toLowerCase()}`}>{VEHICLE_TYPE_LABELS[vehicle.type]}</span>
                        <span className="font-mono text-xs font-bold text-slate-500">{vehicle.registration}</span>
                      </div>
                      <div className="font-bold text-slate-900 text-sm">{vehicle.make} {vehicle.model}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{vehicle.year} · {vehicle.color} · {vehicle.fuel_type}</div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                        <span className="text-xs text-slate-400">{vehicle.branch}</span>
                        <span className="text-sm font-black text-foreground">${vehicle.daily_rate}/day</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold font-heading text-slate-900">Billing & Hire Terms</h2>
                <p className="text-sm text-slate-500 mt-1">Choose how this rental is charged, then set the pickup timing and rate.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Rent Mode</label>
                  <select data-testid="terms-rent-mode" value={terms.rent_mode} onChange={(event) => setTerms((current) => ({ ...current, rent_mode: event.target.value, time_period: event.target.value === "PAYG" ? "WEEKLY" : current.time_period || "DAILY" }))} className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium">
                    {RENT_MODES.map((mode) => <option key={mode} value={mode}>{RENT_MODE_LABELS[mode]}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Billing Period</label>
                  <select data-testid="terms-time-period" value={terms.rent_mode === "PAYG" ? "WEEKLY" : terms.time_period} disabled={terms.rent_mode === "PAYG"} onChange={(event) => setTerms((current) => ({ ...current, time_period: event.target.value }))} className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium disabled:bg-slate-50 disabled:text-slate-400">
                    {TIME_PERIODS.map((period) => <option key={period} value={period}>{TIME_PERIOD_LABELS[period]}</option>)}
                  </select>
                </div>

                <InputField label="Pickup Date & Time" testId="terms-pickup" type="datetime-local" value={terms.pickup_datetime} onChange={(event) => setTerms((current) => ({ ...current, pickup_datetime: event.target.value }))} hint="Invoices start from the hire date." />
                <InputField label={terms.rent_mode === "FIXED_TERM" ? "Planned Return Date & Time" : "Planned Return Date (optional)"} testId="terms-return" type="datetime-local" value={terms.return_datetime} onChange={(event) => setTerms((current) => ({ ...current, return_datetime: event.target.value }))} hint={terms.rent_mode === "FIXED_TERM" ? "Required for fixed term rentals." : "Optional for pay as you go rentals."} />
                <InputField label="Pickup Branch" testId="terms-pickup-branch" value={terms.pickup_branch} onChange={(event) => setTerms((current) => ({ ...current, pickup_branch: event.target.value }))} />
                <InputField label="Return Branch" testId="terms-return-branch" value={terms.return_branch} onChange={(event) => setTerms((current) => ({ ...current, return_branch: event.target.value }))} />

                {terms.rent_mode === "FIXED_TERM" && terms.time_period === "DAILY" ? (
                  <InputField label="Daily Rate ($)" testId="terms-daily-rate" type="number" step="0.01" value={terms.daily_rate} onChange={(event) => setTerms((current) => ({ ...current, daily_rate: parseFloat(event.target.value) || 0 }))} />
                ) : (
                  <InputField label="Weekly Rate ($)" testId="terms-weekly-rate" type="number" step="0.01" value={terms.weekly_rate} onChange={(event) => setTerms((current) => ({ ...current, weekly_rate: parseFloat(event.target.value) || 0 }))} />
                )}

                <InputField label="Bond Amount ($)" testId="terms-bond" type="number" step="0.01" value={terms.bond_amount} onChange={(event) => setTerms((current) => ({ ...current, bond_amount: parseFloat(event.target.value) || 0 }))} />
              </div>

              <p data-testid="terms-date-error" className={`text-sm font-medium ${termsValidationError ? "text-red-600" : "hidden"}`}>
                {termsValidationError || "Dates look good"}
              </p>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Notes</label>
                <textarea data-testid="terms-notes" rows={3} value={terms.notes} onChange={(event) => setTerms((current) => ({ ...current, notes: event.target.value }))} className="w-full px-4 py-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black font-medium resize-none" placeholder="Internal notes or delivery instructions..." />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold font-heading text-slate-900">Customer Contact</h2>
                <p className="text-sm text-slate-500 mt-1">Enter the customer email. UteFlow will send one link for details, licence upload, agreement review, and signature.</p>
              </div>

              <div className="max-w-xl">
                <InputField label="Customer Email" testId="customer-email" type="email" autoComplete="email" value={customerEmail} onChange={(event) => setCustomerEmail(event.target.value)} hint="This email receives the customer completion link." />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold font-heading text-slate-900">Review & Send Customer Link</h2>
                <p className="text-sm text-slate-500 mt-1">This creates the rental shell and immediately sends the customer link for details and signing.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Vehicle</h3>
                  <div className="bg-slate-50 rounded-md p-4 space-y-1 text-sm">
                    <div className="font-bold text-slate-900">{selectedVehicle?.make} {selectedVehicle?.model}</div>
                    <div className="text-slate-600">{selectedVehicle?.registration} · {VEHICLE_TYPE_LABELS[selectedVehicle?.type]}</div>
                    <div className="text-slate-500">{selectedVehicle?.branch}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Customer</h3>
                  <div className="bg-slate-50 rounded-md p-4 space-y-1 text-sm">
                    <div className="font-bold text-slate-900">{customerEmail}</div>
                    <div className="text-slate-500">Customer will complete name, address, licence, and signature from the same link.</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Hire Timing</h3>
                  <div data-testid="review-dates" className="bg-slate-50 rounded-md p-4 space-y-1 text-sm">
                    <div><span className="text-slate-500">Pickup:</span> <span className="font-semibold">{formatDateTime(terms.pickup_datetime)}</span></div>
                    <div><span className="text-slate-500">Planned Return:</span> <span className="font-semibold">{terms.return_datetime ? formatDateTime(terms.return_datetime) : "Open-ended"}</span></div>
                    <div><span className="text-slate-500">Branches:</span> {terms.pickup_branch || "—"} → {terms.return_branch || "—"}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Billing</h3>
                  <div className="bg-slate-50 rounded-md p-4 space-y-1 text-sm">
                    <div><span className="text-slate-500">Rent Mode:</span> {RENT_MODE_LABELS[terms.rent_mode]}</div>
                    <div><span className="text-slate-500">Billing Period:</span> {TIME_PERIOD_LABELS[terms.rent_mode === "PAYG" ? "WEEKLY" : terms.time_period]}</div>
                    <div>
                      <span className="text-slate-500">Rate:</span>{" "}
                      <span className="font-bold text-foreground">
                        {terms.rent_mode === "FIXED_TERM" && terms.time_period === "DAILY"
                          ? `$${Number(terms.daily_rate || 0).toFixed(2)}/day`
                          : `$${Number(terms.weekly_rate || 0).toFixed(2)}/week`}
                      </span>
                    </div>
                    <div><span className="text-slate-500">Bond:</span> ${Number(terms.bond_amount || 0).toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {terms.notes ? <div className="bg-slate-50 rounded-md p-4 text-sm"><span className="text-slate-500 font-semibold">Notes:</span> {terms.notes}</div> : null}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button data-testid="wizard-back" onClick={() => step > 0 ? setStep(step - 1) : navigate("/dashboard")} className="flex items-center gap-2 h-11 px-5 border border-slate-200 rounded-md text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors">
            <CaretLeft weight="bold" className="w-4 h-4" /> {step === 0 ? "Cancel" : "Back"}
          </button>

          {step < STEPS.length - 1 ? (
            <button data-testid="wizard-next" onClick={() => setStep(step + 1)} disabled={!canProceed()} className="flex items-center gap-2 h-11 px-6 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-40 transition-colors">
              Next <CaretRight weight="bold" className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="wizard-create" onClick={handleCreate} disabled={creating} className="flex items-center gap-2 h-11 px-6 bg-emerald-600 text-white rounded-md text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {creating ? "Creating..." : "Create Rental & Send Link"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
